import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import type { UseCartResult } from "./useCart";
import { Slot } from "../slots/Slot";
import { cartExtension, grantedTotal } from "./cart-extension";

/**
 * The cart.
 *
 * Every figure is mono and tabular so columns of money line up and digits do
 * not jitter as a total changes — a total that shifts sideways while a customer
 * watches reads as untrustworthy even when it is correct.
 */

interface CartProps {
  cart: UseCartResult;
  currency: CurrencyFormat;
  showTax: boolean;
  onTender(): void;
  onDiscount(): void;
}

export function Cart({ cart, currency, showTax, onTender, onDiscount }: CartProps) {
  const { lines, priced } = cart;
  const free = grantedTotal(cart);
  const discountOnly = priced.discountTotal - free;

  return (
    <aside className="cart">
      <div className="cart__lines">
        {lines.length === 0 && <p className="cart__empty">No items yet</p>}

        {priced.lines.map((line) => {
          const source = lines.find((l) => l.lineId === line.lineId)!;
          const grant = cart.grants[line.lineId];
          return (
            <article
              key={line.lineId}
              className={`cart__line${grant ? " cart__line--granted" : ""}`}
            >
              <div className="cart__line-main">
                <span className="cart__line-name">
                  {line.itemName}
                  {line.variantName && (
                    <span className="cart__line-variant"> {line.variantName}</span>
                  )}
                </span>
                <span className="cart__line-total money">
                  {formatMoney(line.lineTotal, currency)}
                </span>
              </div>

              {line.modifiers.length > 0 && (
                <p className="cart__line-mods">{line.modifiers.map((m) => m.name).join(", ")}</p>
              )}

              {source.note && <p className="cart__line-note">{source.note}</p>}

              {grant && <p className="cart__line-grant">{grant.label}</p>}

              <div className="cart__line-controls">
                {/* A free line is exactly one unit. Changing its quantity would
                    end the grant, so only Remove is offered. */}
                {!grant && (
                  <>
                    <button
                      type="button"
                      className="cart__step"
                      onClick={() => cart.decrement(line.lineId)}
                      aria-label={`Reduce ${line.itemName}`}
                    >
                      −
                    </button>
                    <span className="cart__qty money">{line.quantity}</span>
                    <button
                      type="button"
                      className="cart__step"
                      onClick={() => cart.increment(line.lineId)}
                      aria-label={`Add another ${line.itemName}`}
                    >
                      +
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="cart__remove"
                  onClick={() => cart.remove(line.lineId)}
                  aria-label={`Remove ${line.itemName}`}
                >
                  Remove
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="cart__totals">
        <Slot id="till.cart.footer" cart={cartExtension(cart)} />

        <div className="cart__row">
          <span>Subtotal</span>
          <span className="money">{formatMoney(priced.subtotal, currency)}</span>
        </div>

        {free > 0 && (
          <div className="cart__row">
            <span>Free items</span>
            <span className="money">−{formatMoney(free as typeof priced.total, currency)}</span>
          </div>
        )}

        {discountOnly > 0 && (
          <div className="cart__row">
            {/* The rate is named, not just the amount. "Discount −₱40.00"
                leaves a cashier no way to check the right one was applied. */}
            <span>
              Discount
              {cart.promo ? ` ${cart.promo.rateBp / 100}%` : cart.statutory ? " (Senior/PWD)" : ""}
            </span>
            <span className="money">
              −{formatMoney(discountOnly as typeof priced.total, currency)}
            </span>
          </div>
        )}

        {/* VAT lines appear only under a VAT regime (FR-027b). A non-VAT
            business must not display a breakdown it does not charge. */}
        {showTax && priced.taxAmount > 0 && (
          <div className="cart__row cart__row--muted">
            <span>VAT included</span>
            <span className="money">{formatMoney(priced.taxAmount, currency)}</span>
          </div>
        )}

        <div className="cart__row cart__row--total">
          <span>Total</span>
          <span className="money">{formatMoney(priced.total, currency)}</span>
        </div>

        <button
          type="button"
          className="cart__discount"
          disabled={cart.isEmpty}
          onClick={onDiscount}
        >
          {cart.promo
            ? `Discount ${cart.promo.rateBp / 100}% applied`
            : cart.statutory
              ? "Senior/PWD discount applied"
              : "Discount"}
        </button>

        <button type="button" className="cart__tender" disabled={cart.isEmpty} onClick={onTender}>
          Take payment
        </button>
      </div>
    </aside>
  );
}

export default Cart;
